import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useIsHydrated } from "shared/lib/react/useIsHydrated"

import { PortfolioPageObj } from "scrivito/Objs/PortfolioPage/PortfolioPageObjClass"
import { ProductCategoryObj } from "scrivito/Objs/ProductCategory/ProductCategoryObjClass"
import { useDebounce } from "shared/lib/performance/useDebounce"
import { useUrlParamStore } from "shared/lib/url/useUrlParams"

import { useProductFilter } from "entities/products/model/useProductFilter"
import { productFilterFormDataSchema } from "entities/products/types/ProductFilter"
import { usePortfolioFilterStore } from "widgets/product-filter/model/usePortfolioFilterStore"

export function usePortfolioPageModel({ page }: { page: PortfolioPageObj }) {
  const { filterState, setFilters } = usePortfolioFilterStore()
  const [searchParams] = useSearchParams()
  const isHydrated = useIsHydrated()

  const { values, setValues } = useUrlParamStore(productFilterFormDataSchema, {
    state: filterState,
    setState: setFilters
  })

  const categories = page
    .orderedChildren()
    ?.filter(
      category => !category.get("isHiddenInPortfolio")
    ) as ProductCategoryObj[]

  // Seeding the debounce with the term from the URL lets a page that is opened with
  // a search show its results right away. The delay only exists to keep typing from
  // re-filtering the whole portfolio on every keystroke.
  const debouncedSearchTerm = useDebounce(
    filterState.searchTerm,
    400,
    searchParams.get("searchTerm")
  )

  const debouncedFilterState = useMemo(
    () => ({
      ...values,
      // The seeded term is browser-only state and must not reach the first render.
      searchTerm: isHydrated ? debouncedSearchTerm : null
    }),
    [values, debouncedSearchTerm, isHydrated]
  )

  const filteredProducts = useProductFilter(debouncedFilterState)

  return {
    defaultValues: values,
    filterState: debouncedFilterState,
    categories,
    products: filteredProducts,
    onChange: setValues
  }
}
